import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchCalculations, createCalculation, importCalculationOld } from '../../store/calculations';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';

const CalculationSearchPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    
    const { 
      data: { 
        value: calculations = [], 
        totalCount = 0, 
        page: currentPage = 0, 
        size: pageSize = 10, 
        totalPages = 1 
      } = {}, 
      loading, 
      error,
      current: { data: currentCalculation, loading: currentLoading } = {}
    } = useSelector((state) => state.calculationsData || {});

    // Initialize from URL params
    const [page, setPage] = useState(parseInt(urlSearchParams.get('page')) || 0);
    const [rowsPerPage, setRowsPerPage] = useState(parseInt(urlSearchParams.get('size')) || 10);
    const [searchParams, setSearchParams] = useState({
        calculationName: urlSearchParams.get('calculationName') || '',
        createdAfter: urlSearchParams.get('createdAfter') || null,
        createdBefore: urlSearchParams.get('createdBefore') || null,
    });
    const [isCreatingCalculation, setIsCreatingCalculation] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        calculationName: ''
    });
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = useRef(null);
    const previousLoadingRef = useRef(currentLoading);
    const users = useSelector(state => state.user?.users || []);

    const getUserFullName = (userId, users) => {
        if (!userId || !users) return 'N/A';
        const user = users.find(u => u.id === userId);
        if (user) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || user.email || 'N/A';
        }
        return 'N/A';
    };

    useEffect(() => {
        handleSearch();
    }, [page, rowsPerPage]);

    // Effect to handle navigation after calculation creation
    useEffect(() => {
        if (isCreatingCalculation && currentCalculation && !currentLoading) {
            setIsCreatingCalculation(false);
            // Refresh the search results to show the new calculation
            const searchParamsObj = {
                calculationName: searchParams.calculationName || undefined,
                createdAfter: formatToInstant(searchParams.createdAfter),
                createdBefore: formatToInstant(searchParams.createdBefore),
                page: page,
                size: rowsPerPage,
            };
            // Remove undefined values
            Object.keys(searchParamsObj).forEach(key => 
                searchParamsObj[key] === undefined && delete searchParamsObj[key]
            );
            dispatch(fetchCalculations(searchParamsObj));
            navigate(`/calculations/${currentCalculation.id}`);
        }
    }, [currentCalculation, currentLoading, isCreatingCalculation, navigate, dispatch, searchParams, page, rowsPerPage]);

    const formatToInstant = useCallback((date) => {
        if (!date) return undefined;
        const d = new Date(date);
        // Ensure we have a valid date
        if (isNaN(d.getTime())) return undefined;
        // Format to ISO string with Z suffix - backend expects java.time.Instant format (e.g., 2025-10-21T21:07:00Z)
        return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
    }, []);

    const handleDateChange = (date, field) => {
        // If date is null, set it to null, otherwise ensure it's a valid date
        const newDate = date ? new Date(date) : null;
        setSearchParams(prev => ({
            ...prev,
            [field]: newDate && !isNaN(newDate.getTime()) ? newDate : null
        }));
    };

    const handleSearch = (e) => {
        if (e) {
            e.preventDefault();
        }
        
        // Prepare the search parameters
        const searchParamsObj = {
            calculationName: searchParams.calculationName || undefined,
            createdAfter: formatToInstant(searchParams.createdAfter),
            createdBefore: formatToInstant(searchParams.createdBefore),
            page: page,
            size: rowsPerPage,
        };

        // Remove undefined values
        Object.keys(searchParamsObj).forEach(key => 
            searchParamsObj[key] === undefined && delete searchParamsObj[key]
        );

        // Update URL params
        const newUrlParams = new URLSearchParams();
        if (searchParams.calculationName) newUrlParams.set('calculationName', searchParams.calculationName);
        if (searchParams.createdAfter) newUrlParams.set('createdAfter', searchParams.createdAfter);
        if (searchParams.createdBefore) newUrlParams.set('createdBefore', searchParams.createdBefore);
        newUrlParams.set('page', page.toString());
        newUrlParams.set('size', rowsPerPage.toString());
        setUrlSearchParams(newUrlParams);

        // Dispatch the action
        dispatch(fetchCalculations(searchParamsObj));
    };

    const handleChangePage = (newPage) => {
        setPage(newPage);
        // Update URL param
        const newUrlParams = new URLSearchParams(urlSearchParams);
        newUrlParams.set('page', newPage.toString());
        setUrlSearchParams(newUrlParams);
    };

    const handleChangeRowsPerPage = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setRowsPerPage(newSize);
        setPage(0);
        // Update URL params
        const newUrlParams = new URLSearchParams(urlSearchParams);
        newUrlParams.set('size', newSize.toString());
        newUrlParams.set('page', '0');
        setUrlSearchParams(newUrlParams);
    };

    const handleRowClick = (id) => {
        navigate(`/calculations/${id}`);
    };

    const handleShowCreateForm = useCallback(() => {
        setShowCreateForm(true);
        setCreateFormData({
            calculationName: ''
        });
    }, []);

    const handleCreateFormChange = useCallback((field, value) => {
        setCreateFormData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const handleCreateCalculation = useCallback(() => {
        if (!createFormData.calculationName.trim()) {
            alert(t('calculation.calculationName') + ' ' + t('common.required'));
            return;
        }
        
        const payload = {
            calculationName: createFormData.calculationName.trim()
        };
        
        setIsCreatingCalculation(true);
        setShowCreateForm(false);
        dispatch(createCalculation(payload));
    }, [dispatch, createFormData]);

    const handleCancelCreate = useCallback(() => {
        setShowCreateForm(false);
        setCreateFormData({
            calculationName: ''
        });
    }, []);

    const handleImportClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback((event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            alert(t('calculation.importCalculation') + ': Please select a valid Excel file');
            return;
        }
        
        setIsImporting(true);
        dispatch(importCalculationOld(file));
        
        // Reset file input after dispatch
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [dispatch]);
    
    // Handle import success - detect when loading changes from true to false and we have a calculation
    useEffect(() => {
        if (isImporting && previousLoadingRef.current && !currentLoading && currentCalculation) {
            setIsImporting(false);
            // Refresh the calculations list after import
            const searchParamsObj = {
                calculationName: searchParams.calculationName || undefined,
                createdAfter: formatToInstant(searchParams.createdAfter),
                createdBefore: formatToInstant(searchParams.createdBefore),
                page: page,
                size: rowsPerPage,
            };
            Object.keys(searchParamsObj).forEach(key => 
                searchParamsObj[key] === undefined && delete searchParamsObj[key]
            );
            dispatch(fetchCalculations(searchParamsObj));
        }
        previousLoadingRef.current = currentLoading;
    }, [currentLoading, isImporting, currentCalculation, searchParams, page, rowsPerPage, formatToInstant, dispatch]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
        } catch (ex) {
            console.error(ex);
            return "";
        }

    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2
        }).format(value || 0);
    };

    const calculateTotal = (items = []) => {
        return items.reduce((sum, item) => sum + (item.quantity * (item.sellPrice || 0)), 0);
    };

    const handleClearFilters = () => {
        setSearchParams({
            calculationName: '',
            createdAfter: null,
            createdBefore: null
        });
        setPage(0);
        const newUrlParams = new URLSearchParams();
        newUrlParams.set('page', '0');
        newUrlParams.set('size', rowsPerPage.toString());
        setUrlSearchParams(newUrlParams);
        dispatch(fetchCalculations({ page: 0, size: rowsPerPage }));
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h1 className="text-3xl font-bold text-[#34495e] mb-6">{t('calculation.title')}</h1>
                
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('calculation.calculationName')}</label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                                value={searchParams.calculationName}
                                onChange={(e) => setSearchParams({ ...searchParams, calculationName: e.target.value })}
                                placeholder={t('calculation.searchCalculations')}
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.createdAt')} {t('common.after')}</label>
                            <input
                                type="datetime-local"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                                value={searchParams.createdAfter || ''}
                                onChange={(e) => setSearchParams({ 
                                    ...searchParams, 
                                    createdAfter: e.target.value || null 
                                })}
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.createdAt')} {t('common.before')}</label>
                            <input
                                type="datetime-local"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                                value={searchParams.createdBefore || ''}
                                onChange={(e) => setSearchParams({ 
                                    ...searchParams, 
                                    createdBefore: e.target.value || null 
                                })}
                            />
                        </div>
                    </div>
                    
                    <div className="flex gap-3">
                        <button
                            onClick={handleSearch}
                            className="flex items-center gap-2 bg-[#34495e] hover:bg-[#2c3e50] text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            title={t('common.search')}
                        >
                            <MagnifyingGlassIcon className="h-5 w-5" />
                            {t('common.search')}
                        </button>
                        <button
                            onClick={handleClearFilters}
                            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            title={t('common.clear')}
                        >
                            <XMarkIcon className="h-5 w-5" />
                            {t('common.clear')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    ID
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('calculation.calculationName')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('common.createdAt')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('common.updatedAt')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('common.createdBy')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('common.updatedBy')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('calculation.addItem')}
                                </th>
                                <th scope="col" className="relative px-6 py-3">
                                    <span className="sr-only">{t('common.actions')}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#34495e] mb-4"></div>
                                            <p className="text-gray-500">{t('calculation.loadingCalculations')}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : calculations.length > 0 ? (
                                calculations.map((calculation) => (
                                    <tr 
                                        key={calculation.id} 
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleRowClick(calculation.id)}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {calculation.id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {calculation.calculationName || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(calculation.createdAt)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(calculation.updatedAt)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {getUserFullName(calculation.createdBy, users) || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {getUserFullName(calculation.updatedBy, users) || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                            {calculation.items?.length || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                            </svg>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <p className="text-gray-500 font-medium">{t('calculation.noCalculations')}</p>
                                            <p className="text-gray-400 text-sm mt-1">{t('board.tryAdjusting')}</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                
                {calculations.length > 0 && (
                    <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200">
                        <div className="text-sm text-gray-700">
                            Showing <span className="font-medium">{(currentPage * pageSize) + 1}</span> to{' '}
                            <span className="font-medium">
                                {Math.min((currentPage + 1) * pageSize, totalCount)}
                            </span>{' '}
                            of <span className="font-medium">{totalCount}</span> results
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 0}
                                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                                        page === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="sr-only">Previous</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i;
                                    } else if (page < 3) {
                                        pageNum = i;
                                    } else if (page > totalPages - 4) {
                                        pageNum = totalPages - 5 + i;
                                    } else {
                                        pageNum = page - 2 + i;
                                    }
                                    
                                    if (pageNum < 0 || pageNum >= totalPages) return null;
                                    
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setPage(pageNum)}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                page === pageNum
                                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            {pageNum + 1}
                                        </button>
                                    );
                                })}
                                
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={page >= totalPages - 1}
                                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                                        page >= totalPages - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="sr-only">Next</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Create Calculation Section */}
            <div className="mt-6">
                {!showCreateForm ? (
                    <div className="flex gap-3">
                        <button
                            onClick={handleShowCreateForm}
                            disabled={currentLoading}
                            className="bg-[#34495e] hover:bg-[#2c3e50] disabled:bg-gray-400 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            title={t('calculation.createCalculation')}
                        >
                            {t('calculation.createCalculation')}
                        </button>
                        
                        <button
                            onClick={handleImportClick}
                            disabled={isImporting || currentLoading}
                            className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            title={t('calculation.importCalculation')}
                        >
                            {isImporting ? t('common.loading') : t('calculation.importCalculation')}
                        </button>
                        
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h3 className="text-xl font-semibold text-[#34495e] mb-4">{t('calculation.createCalculation')}</h3>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('calculation.calculationName')} *
                            </label>
                            <input
                                type="text"
                                value={createFormData.calculationName}
                                onChange={(e) => handleCreateFormChange('calculationName', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                                placeholder={t('calculation.searchCalculations')}
                                required
                            />
                        </div>
                        
                        <div className="flex gap-3">
                            <button
                                onClick={handleCreateCalculation}
                                disabled={currentLoading || !createFormData.calculationName.trim()}
                                className="bg-[#34495e] hover:bg-[#2c3e50] disabled:bg-gray-400 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            >
                                {currentLoading ? t('common.creating') : t('common.create')}
                            </button>
                            
                            <button
                                onClick={handleCancelCreate}
                                disabled={currentLoading}
                                className="bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-800 font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CalculationSearchPage;
